import { useCallback, useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ProviderType } from '@typesApp/Insurer'
import { useEnvironment } from '@hooks/useEnvironment'
import { connectorActionModuleSelector } from '@redux/slices/connectorSlice'
import { useToggleWidgetContext } from '@components/widgetContext/useWidgetContext'
import { getErrorMessage } from '@utils/errorUtil'
import { InteractionContext } from './context'
import {
  ContactUsVariant,
  ErrorInteractionSubType,
  SelectProductSubType,
  InteractionEventPayload,
  InteractionName,
  InteractionSubtype,
  InteractionType,
  SyncInteractionSubType,
  ToggleInteractionSubType,
  ErrorInteractionDetails,
} from './types'

type UseInteractionContextReturn = {
  pushOpenPanelInteraction: () => void
  pushSelectInsurerInteraction: (provider: ProviderType, insurerName: string) => void
  pushGetAttachmentInteraction: () => void
  pushContactUsInteraction: (variant: ContactUsVariant) => void
  pushSyncInsuranceInteraction: (subtype: SyncInteractionSubType) => void
  pushShoppingAction: (ctaLabel: string) => void
  pushRemoveBenefitsInteraction: () => void
  pushFallbackInteraction: (subtype?: ErrorInteractionSubType, details?: ErrorInteractionDetails) => void
  pushToggleInteraction: (subtype: ToggleInteractionSubType) => void
  pushSelectProductInteraction: (subtype: SelectProductSubType, productName?: string) => void
  pushSelectInsurerLogoInteraction: (providerType: ProviderType, insurerName: string) => void
}

export const useInteractionContext = (): UseInteractionContextReturn => {
  const { name, type } = useContext(InteractionContext)
  const toggleContext = useToggleWidgetContext()
  const { translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace, { keyPrefix: 'InteractionEvents' })
  const actionModule = useSelector(connectorActionModuleSelector)

  // for multiple toggle instances - use toggle name as event source if available
  const source = toggleContext?.name

  const pushEvent = useCallback(
    (
      interactionName: InteractionName,
      interactionType: InteractionType | undefined,
      subtype: InteractionSubtype | undefined,
      arg?: Record<string, string>,
      details?: string
    ) => {
      if (typeof actionModule?.onInteractionEvent !== 'function' || !interactionName || !interactionType || !subtype) {
        return
      }

      const prefix = interactionName ?? name
      const typePrefix = `${prefix}.${interactionType ?? type}`

      let payload: InteractionEventPayload = {
        name: t(`${prefix}.name`),
        type: t(`${typePrefix}.name`),
        subtype: arg ? t(`${typePrefix}.${subtype}`, arg) : t(`${typePrefix}.${subtype}`),
        details,
      }

      if (source) {
        payload = {
          ...payload,
          source,
        }
      }

      console.debug('directbilling interaction event pushed ', payload)

      actionModule.onInteractionEvent(payload).catch(error => {
        console.warn('Error on push insurance interaction event', error)
      })
    },
    [name, type, t, source, actionModule]
  )

  return useMemo<UseInteractionContextReturn>(() => {
    const pushOpenPanelInteraction = () => {
      pushEvent(name, type, 'open-panel')
    }

    const pushContactUsInteraction = (variant: ContactUsVariant) => {
      pushEvent(name, type, `contact-${variant}` as InteractionSubtype)
    }

    const pushGetAttachmentInteraction = () => {
      pushEvent(name, type, 'download-form')
    }

    const pushSelectInsurerInteraction = (providerType: ProviderType, insurerName: string) => {
      const arg = {
        providerType: `${providerType.toLowerCase()}`,
        insurerName: `${insurerName?.trim()?.replaceAll(' ', '_')}`,
      }

      let eventType = type

      // The context is not updated until AFTER the insurer selection
      // is made, hence the providerType needs to take precedence
      // over the value of 'type'.
      //
      // providerType is ignored when type is 'check-eligibility'
      // because the 'check-eligibility' event does not contain the providerType.

      if (type !== 'check-eligibility') {
        if (providerType === ProviderType.IN_NETWORK) {
          eventType = 'in-network'
        } else if (providerType === ProviderType.OUT_OF_NETWORK) {
          eventType = 'out-of-network'
        }
      }

      pushEvent(name, eventType, 'select-insurer', arg)
    }

    const pushSyncInsuranceInteraction = (subtype: SyncInteractionSubType) => {
      pushEvent(name, type, subtype)
    }

    const pushRemoveBenefitsInteraction = () => {
      pushEvent(name, type, 'remove-insurance-benefit')
    }

    const pushShoppingAction = (ctaLabel: string) => {
      pushEvent(name, type, 'shopping-action', { action: ctaLabel?.replaceAll(' ', '_') })
    }

    const pushFallbackInteraction = (
      subtype: ErrorInteractionSubType = 'fallback',
      details?: ErrorInteractionDetails
    ) => {
      let errorMessage: string | undefined = undefined

      // push more detailed error message if available
      if (subtype === 'fallback' && details) {
        const error = details.error ? getErrorMessage(details.error) : t('fallback-details.unavailable')
        errorMessage = details.reason
          ? t(`fallback-details.${details.reason}`, { error })
          : t('fallback-details.generic-error', { error })
        pushEvent('insurance-panel', 'technical-error', subtype, undefined, errorMessage)
        return
      }

      pushEvent('insurance-panel', 'technical-error', subtype)
    }

    const pushToggleInteraction = (subtype: ToggleInteractionSubType) => {
      pushEvent(name, type, subtype)
    }

    const pushSelectProductInteraction = (subtype: SelectProductSubType, productName?: string) => {
      pushEvent('insurance-panel', 'select-product', subtype, { productName: productName?.replaceAll(' ', '_') ?? '' })
    }

    const pushSelectInsurerLogoInteraction = (providerType: ProviderType, insurerName: string) => {
      const arg = {
        providerType: `${providerType.toLowerCase()}`,
        insurerName: `${insurerName?.trim()?.replaceAll(' ', '_')}`,
      }
      pushEvent(name, type, 'select-insurer-logo', arg)
    }

    return {
      pushOpenPanelInteraction,
      pushSelectInsurerInteraction,
      pushContactUsInteraction,
      pushGetAttachmentInteraction,
      pushSyncInsuranceInteraction,
      pushShoppingAction,
      pushRemoveBenefitsInteraction,
      pushFallbackInteraction,
      pushToggleInteraction,
      pushSelectProductInteraction,
      pushSelectInsurerLogoInteraction,
    }
  }, [pushEvent, name, type, t])
}
